// frontend/src/pages/manufacturing/v2/components/ScrapStripV2.jsx
//
// The scrap strip at the top of the shop-floor terminal: pick an item, a
// quantity and a reason, and it is scrapped from stock.
//
// Suggestions: clicking into the search (empty) lists what is likely to be
// scrapped given the work orders on screen — due today / overdue first, then
// the rest of the queue — each WO's unit and every component it uses, with
// the quantity pre-filled to one unit's worth. Typing narrows that list and
// falls back to the whole item catalogue (GET /api/v2/scrap/suggestions/).
//
// What the server does (services/scrap.py): records the scrap and takes the
// replacement's raw material out of stock (a bought-in item leaves stock
// itself). No work order is created, revised or removed.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Chip, CircularProgress, Paper, Stack,
  TextField, Typography,
} from '@mui/material';
import { DeleteSweep } from '@mui/icons-material';
import { toast } from 'react-toastify';

import { v2 } from '../../../../services/api';
import { describeConsumption } from './ScrapComponentDialogV2';

const DEBOUNCE_MS = 250;
const round3 = (v) => Math.round((parseFloat(v) || 0) * 1000) / 1000;

const GROUP_ORDER = { today: 0, screen: 1, catalogue: 2 };
const GROUP_LABELS = {
  today: 'Due today / overdue',
  screen: 'On this screen',
  catalogue: 'All items',
};

function groupOf(option) {
  if (option.role === 'catalogue') return 'catalogue';
  return option.due_today ? 'today' : 'screen';
}

// Second line of a suggestion: what it's for, then the order and customer
// it's being made for — names only, no codes or WO numbers (easier to
// read at a glance).
function reasonLine(option) {
  if (option.role === 'catalogue') return 'from the item list';
  const wos = option.work_orders || [];
  const first = wos[0];
  if (!first) return '';
  const what = option.role === 'output'
    ? 'being made'
    : first.parent_item_name || first.parent_item_code
      ? `goes into ${first.parent_item_name || first.parent_item_code}`
      : 'component';
  const who = [first.customer_order_number, first.customer_name].filter(Boolean).join(' · ');
  const more = option.work_order_count > 1 ? ` +${option.work_order_count - 1} more` : '';
  return `${what}${who ? ` · ${who}` : ''}${more}`;
}

const byName = (a, b) => (a.item_name || a.item_code || '').localeCompare(
  b.item_name || b.item_code || '', undefined, { sensitivity: 'base' },
);

export default function ScrapStripV2({ filters, onRecorded }) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [selected, setSelected] = useState(null);
  const [quantity, setQuantity] = useState('');
  const [reason, setReason] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const requestId = useRef(0);
  const debounceRef = useRef(null);

  const filtersKey = JSON.stringify(filters || {});

  const fetchSuggestions = useCallback(async (q) => {
    const id = ++requestId.current;
    setLoading(true);
    try {
      const params = { ...(filters || {}) };
      if (q) params.q = q;
      const res = await v2.scrap.suggestions(params);
      if (id !== requestId.current) return;
      const rows = res.data?.results || [];
      // Alphabetical by name within each group.
      rows.sort((a, b) => (GROUP_ORDER[groupOf(a)] - GROUP_ORDER[groupOf(b)]) || byName(a, b));
      setOptions(rows);
    } catch (err) {
      console.error('ScrapStripV2: suggestions failed', err);
      if (id === requestId.current) setOptions([]);
    } finally {
      if (id === requestId.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersKey]);

  // Refetch as the operator types (debounced); on open with nothing typed
  // the list is the plain suggestion set.
  useEffect(() => {
    if (!open) return undefined;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(inputValue.trim()), inputValue ? DEBOUNCE_MS : 0);
    return () => clearTimeout(debounceRef.current);
  }, [open, inputValue, fetchSuggestions]);

  // Preview what the scrap takes from stock.
  useEffect(() => {
    if (!selected) { setPreview(null); return undefined; }
    const qty = parseFloat(quantity);
    if (!(qty > 0)) { setPreview(null); return undefined; }
    let cancelled = false;
    (async () => {
      try {
        const res = await v2.scrap.preview({ item: selected.item_id, quantity: qty });
        if (!cancelled) setPreview(res.data);
      } catch (err) {
        if (!cancelled) setPreview(null);
      }
    })();
    return () => { cancelled = true; };
  }, [selected, quantity]);

  const qtyNum = parseFloat(quantity);
  const canSubmit = !!selected && qtyNum > 0 && reason.trim().length > 0 && !busy;

  const reset = () => {
    setSelected(null);
    setInputValue('');
    setQuantity('');
    setReason('');
    setPreview(null);
  };

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      const firstWo = (selected.work_orders || [])[0];
      const res = await v2.scrap.record({
        item: selected.item_id,
        quantity: String(qtyNum),
        reason: reason.trim(),
        work_order: firstWo ? firstWo.id : undefined,
      });
      const consumed = describeConsumption(res.data?.consumed);
      toast.success(
        `Scrapped ${round3(qtyNum)} × ${selected.item_code}`
        + (consumed ? ` — took ${consumed} from stock` : ''),
      );
      reset();
      if (onRecorded) onRecorded(res.data);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.error || 'Failed to record scrap');
    } finally {
      setBusy(false);
    }
  };

  const consumption = useMemo(() => describeConsumption(preview?.consumes), [preview]);

  return (
    <Paper
      variant="outlined"
      data-testid="scrap-strip"
      sx={{
        p: 1.5, mb: 2, borderColor: 'warning.light',
        bgcolor: (t) => (t.palette.mode === 'dark' ? 'rgba(255,167,38,0.08)' : 'rgba(255,167,38,0.06)'),
      }}
    >
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pt: 1, minWidth: 90 }}>
          <DeleteSweep color="warning" />
          <Typography variant="subtitle2" fontWeight={700}>Scrap</Typography>
        </Box>

        <Autocomplete
          size="small"
          sx={{ flex: 2, minWidth: 280 }}
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          options={options}
          loading={loading}
          value={selected}
          inputValue={inputValue}
          onInputChange={(_, v, cause) => {
            if (cause === 'reset' && !v) return;
            setInputValue(v);
          }}
          onChange={(_, v) => {
            setSelected(v);
            if (v) {
              setInputValue(v.item_name || v.item_code);
              setQuantity(String(round3(v.suggested_quantity || 1)));
            } else {
              setQuantity('');
            }
          }}
          // Server already matched the typed text; keep every row it sent.
          filterOptions={(x) => x}
          getOptionLabel={(o) => (o ? (o.item_name || o.item_code) : '')}
          isOptionEqualToValue={(a, b) => a.item_id === b.item_id}
          groupBy={groupOf}
          renderGroup={(params) => (
            <li key={params.key}>
              <Typography
                variant="overline"
                sx={{ px: 2, pt: 1, display: 'block', color: 'text.secondary', lineHeight: 1.5 }}
              >
                {GROUP_LABELS[params.group] || params.group}
              </Typography>
              <ul style={{ padding: 0 }}>{params.children}</ul>
            </li>
          )}
          renderOption={(props, o) => (
            <li {...props} key={o.item_id} data-testid={`scrap-suggestion-${o.item_id}`}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={600} noWrap>
                    {o.item_name || o.item_code}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                    {reasonLine(o)}
                  </Typography>
                </Box>
                {o.role !== 'catalogue' && (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`${round3(o.suggested_quantity)} per unit`}
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                )}
              </Box>
            </li>
          )}
          noOptionsText={loading ? 'Looking…' : 'Nothing matches — try an item code'}
          renderInput={(params) => (
            <TextField
              {...params}
              label="What was scrapped?"
              placeholder="Click for suggestions, or type an item code / name"
              inputProps={{ ...params.inputProps, 'data-testid': 'scrap-strip-search' }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? <CircularProgress color="inherit" size={16} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />

        <TextField
          size="small"
          label="Qty"
          type="number"
          value={quantity}
          onChange={(e) => setQuantity(e.target.value)}
          inputProps={{ min: 0, step: 'any', 'data-testid': 'scrap-strip-qty' }}
          sx={{ width: 110 }}
        />
        <TextField
          size="small"
          label="Reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          inputProps={{ 'data-testid': 'scrap-strip-reason' }}
          placeholder="e.g. chipped edge"
          sx={{ flex: 1, minWidth: 180 }}
        />
        <Button
          variant="contained"
          color="warning"
          onClick={submit}
          disabled={!canSubmit}
          data-testid="scrap-strip-submit"
          sx={{ height: 40 }}
        >
          {busy ? 'Scrapping…' : 'Scrap'}
        </Button>
      </Box>

      {selected && qtyNum > 0 && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
          <Alert severity="info" sx={{ py: 0, flex: 1 }} data-testid="scrap-strip-preview">
            {preview?.bought_in ? (
              <>Takes <strong>{round3(qtyNum)} × {selected.item_code}</strong> from stock (bought in). No work order is changed.</>
            ) : consumption ? (
              <>Takes <strong>{consumption}</strong> from stock to make it again. No work order is changed.</>
            ) : (
              <>Working out what the remake needs…</>
            )}
          </Alert>
        </Stack>
      )}
    </Paper>
  );
}
